import { Global } from '@emotion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoopLogo } from 'src/common/components/icons/LoopLogo';
import { z } from 'zod';
import { ExportModeProvider } from '@/common/components/ui/swimlane/context';
import { LegendFooter } from '../published/LegendFooter';
import { PublishedSwimlane } from '../published/PublishedSwimlane';
import { exportTransparentGlobalStyles } from './ExportSwimlanePage.styles';
import { getBooleanQueryParam, getEffectivePreferences } from './exportUtils';
import { useExportSwimlane } from './useExportSwimlane';

/**
 * Search parameter schema for token-authenticated swimlane export routes.
 *
 * This is an internal route called exclusively by the backend export worker with a JWT token.
 * We use z.coerce.string() to handle query parameters that may be parsed as numbers or booleans
 * by TanStack Router, then rely on component logic to interpret the string values.
 *
 * Required query parameters:
 * - token: JWT export token for authentication
 *
 * Optional query parameters for export customization:
 * - title: '0' to hide title, '1' to show (default: show)
 * - branding: '0' to hide branding/logo, '1' to show (default: show)
 * - date: '0' to hide date, '1' to show (default: show)
 * - legend: '0' to hide legend, '1' to show (default: show)
 * - vision: Custom vision statement text
 * - transparent: '1' to enable transparent background, '0' for opaque (default: opaque)
 */
export const internalSwimlaneExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  legend: z.coerce.string().optional(),
  vision: z.string().optional(),
  transparent: z.coerce.string().optional(),
});

export type InternalSwimlaneExportSearchParams = z.infer<typeof internalSwimlaneExportSearchSchema>;

export interface ExportSwimlanePageProps {
  viewId: number;
  visualizationId: number;
  searchParams: InternalSwimlaneExportSearchParams;
  /**
   * Export mode passed to ExportModeProvider. Defaults to 'png' to preserve
   * existing PNG route behavior. The SVG route passes 'svg' so the swimlane
   * picks the static (non-virtualized) export layout.
   */
  exportMode?: 'png' | 'svg';
}

/**
 * Export page component for token-authenticated visualizations
 * This component is used for generating exports with JWT token authentication.
 *
 * Unlike PublishedSwimlanePage which loads data from public S3 artifacts,
 * this component uses useExportSwimlane hook which makes an authenticated
 * API call to fetch live data for export rendering.
 *
 * Always in export mode - query parameters control visibility of header/footer elements.
 */
export function ExportSwimlanePage({
  viewId,
  visualizationId,
  searchParams,
  exportMode = 'png',
}: ExportSwimlanePageProps) {
  const { t, i18n } = useTranslation();
  const result = useExportSwimlane({
    viewId,
    visualizationId,
    token: searchParams.token,
  });

  const transparentBackground = getBooleanQueryParam(searchParams.transparent, false);

  useEffect(() => {
    if (transparentBackground) {
      document.documentElement.classList.add('export-transparent');
    }
    return () => {
      document.documentElement.classList.remove('export-transparent');
    };
  }, [transparentBackground]);

  switch (result.status) {
    case 'loading':
      return (
        <output className="flex items-center justify-center h-screen text-xl">
          {t(($) => $.views.published.loading)}
        </output>
      );

    case 'error':
      return (
        <div
          className="flex items-center justify-center h-screen text-xl text-text-error"
          role="alert"
        >
          {t(($) => $.views.published.errorLoading, { message: result.message })}
        </div>
      );

    case 'success':
      break; // Fall through to render content
  }

  const { metadata, legendItems } = result.data;

  const preferences = getEffectivePreferences(metadata, searchParams);

  const visionStatement =
    searchParams.vision !== undefined ? searchParams.vision : metadata.visionStatement;

  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  // Export mode styles - allow scrolling for screenshot capture
  const rootContainerClass = 'h-screen w-screen flex flex-col';
  const swimlaneContainerClass = 'flex-1';

  const publishDateValue = metadata.publishedAt || metadata.createdAt;
  const publishDate = publishDateValue
    ? new Date(publishDateValue).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <ExportModeProvider mode={exportMode}>
      {transparentBackground && <Global styles={exportTransparentGlobalStyles} />}
      <div
        className={rootContainerClass}
        style={{ backgroundColor: 'var(--export-bg-color, white)' }}
        data-export-target="root"
      >
        {showHeader && (
          <header
            className="flex-none px-8 pt-6 pb-4"
            style={{
              backgroundColor: 'var(--export-bg-color, white)',
            }}
            data-export-target="header"
          >
            {preferences.showTitle && (
              <h1
                data-testid="swimlane-export-title"
                className="text-2xl font-bold text-text-neutral-primary mb-2"
              >
                {metadata.title}
              </h1>
            )}
            {preferences.showVisionStatement && visionStatement && (
              <div>
                <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                  {visionStatement}
                </p>
              </div>
            )}
          </header>
        )}

        <div className={swimlaneContainerClass}>
          <PublishedSwimlane data={result.data} transparentBackground={transparentBackground} />
        </div>

        {(preferences.showPublishDate ||
          preferences.showTempoBranding ||
          preferences.showLegend) && (
          <footer
            className="flex-none py-4 w-full"
            style={{
              backgroundColor: 'var(--export-bg-color, white)',
            }}
            data-export-target="footer"
          >
            {preferences.showLegend && legendItems.length > 0 && (
              <nav
                aria-label="Roadmap legend"
                className="w-full mb-3"
                data-component="legend-wrapper"
                data-export-target="footer-legend"
              >
                <LegendFooter items={legendItems} />
              </nav>
            )}

            <div className="flex items-center justify-between px-8">
              <div className="text-sm text-text-neutral-secondary">
                {preferences.showPublishDate && publishDate && (
                  <time dateTime={publishDateValue} data-export-target="footer-date">
                    {t(($) => $.views.published.publishedOn, { date: publishDate })}
                  </time>
                )}
              </div>
              <div className="flex items-center gap-3">
                {preferences.showTempoBranding && (
                  <div className="flex items-center gap-2" data-export-target="footer-branding">
                    <LoopLogo width={24} height={24} />
                    <span className="text-sm font-semibold text-text-neutral-primary">
                      {t(($) => $.views.export.tempoBrandingName)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </footer>
        )}
      </div>
    </ExportModeProvider>
  );
}
